/**
 * auto-captions filler PREVIEW-BEFORE-COMMIT.
 *
 * Descript-style review-before-remove: when the user hits "Apply cleanup",
 * we don't drop fillers immediately. Instead each filler/repeat candidate is
 * decorated inline in the editor with a strike-through + "keep" pill so the
 * user can scan the proposed deletions and uncheck anything the cleaner got
 * wrong. Only when the user clicks "Confirm" do the keepers stick and the
 * rest get removed via __acCleaner.apply().
 *
 * Public API:
 *   window.__acFillerPreview.isEnabled()
 *     bool — preview-mode toggle. Default true. Stored in localStorage
 *     under "ac_filler_preview_v1" so power users can disable once.
 *
 *   window.__acFillerPreview.setEnabled(on)
 *     persists the toggle.
 *
 *   window.__acFillerPreview.detectPlan(words, opts)
 *     thin wrapper over __acCleaner.detect — returns { fillers, silences,
 *     totalSecs, totalWords } in the documented shape.
 *
 *   window.__acFillerPreview.renderInlineDecorations(plan, opts)
 *     walks the #ac-editor DOM looking up word-spans by data-w-idx and
 *     wraps each filler-plan entry's word in <span class="ac-filler-pending">
 *     with a strike + keep-pill (×).
 *
 *   window.__acFillerPreview.applyPlan(words, plan, opts)
 *     excludes user-deselected items (per kept indices we tracked) and
 *     calls __acCleaner.apply().
 *
 *   window.__acFillerPreview.cancelPreview()
 *     clears the inline pending decorations + hides the review banner.
 *
 *   window.__acFillerPreview.getPendingKeepIndices()
 *     returns the Set of indices the user has marked "keep" — used by the
 *     pipeline + tests to verify state.
 *
 * The module is editor-DOM aware (it knows about #ac-editor and data-w-idx)
 * because the pipeline needs a single shared way to surface pending deletions
 * inside the existing transcript editor. It deliberately does NOT own the
 * "Confirm" / "Cancel" banner buttons themselves — pipeline.js wires those
 * to applyPlan() / cancelPreview() because banner copy comes from the I18N
 * map only pipeline.js has direct access to.
 */
(function(){
    'use strict';

    var STORAGE_KEY = 'ac_filler_preview_v1';
    // In-memory state for the current preview pass. Cleared by cancelPreview()
    // or by applyPlan() when the user commits.
    var current = {
        plan: null,          // detect() result
        keep: null,          // Set<wordIndex>
        decoratedNodes: [],  // [{ originalNode, replacement }] for cleanup
    };

    function isEnabled() {
        try {
            var v = localStorage.getItem(STORAGE_KEY);
            // Default true — review-mode is opt-out, not opt-in.
            if (v == null) return true;
            return v === '1';
        } catch (_) { return true; }
    }

    function setEnabled(on) {
        try { localStorage.setItem(STORAGE_KEY, on ? '1' : '0'); } catch (_) {}
    }

    function detectPlan(words, opts) {
        if (!window.__acCleaner || typeof window.__acCleaner.detect !== 'function') {
            return { fillers: [], silences: [], totalSecs: 0, totalWords: 0 };
        }
        var raw = window.__acCleaner.detect(words, opts || {});
        // Normalise to the documented shape — strip out the duplicate `repeats`
        // helper field so consumers only see the canonical list.
        return {
            fillers: (raw.fillers || []).map(function(f){
                return {
                    wordIndex: f.wordIndex,
                    text: f.text,
                    reason: f.reason || 'filler',
                    start: f.start,
                    end: f.end,
                };
            }),
            silences: (raw.silences || []).map(function(s){
                return { start: s.start, end: s.end, secs: s.secs };
            }),
            totalSecs: raw.totalSecs || 0,
            totalWords: raw.totalWords || (raw.fillers || []).length,
        };
    }

    /**
     * renderInlineDecorations(plan, opts)
     *
     * Walks #ac-editor (or opts.editorEl if provided for tests) and wraps
     * each plan.fillers[i].wordIndex's word-span in a pending decoration.
     *
     * Look-up strategy:
     *   - First try `[data-w-idx="<idx>"]` (what tests / new renders use)
     *   - Fall back to indexing by occurrence order if the editor uses a
     *     legacy markup without data-w-idx.
     */
    function renderInlineDecorations(plan, opts) {
        opts = opts || {};
        var editor = opts.editorEl || (typeof document !== 'undefined' && document.getElementById('ac-editor'));
        if (!editor || !plan || !plan.fillers || !plan.fillers.length) return 0;

        // Reset any prior decoration before re-applying — avoid double-wrap.
        clearDecorations(editor);

        current.plan = plan;
        current.keep = current.keep || new Set();
        current.decoratedNodes = [];

        var keepLabel = opts.keepLabel || 'keep';
        var keepAria = opts.keepAriaTpl || 'Keep "{text}"';
        var byIdx = editor.querySelectorAll('[data-w-idx]');
        var hasDataIdx = byIdx.length > 0;

        var decorated = 0;
        for (var i = 0; i < plan.fillers.length; i++) {
            var hit = plan.fillers[i];
            var target = null;
            if (hasDataIdx) {
                target = editor.querySelector('[data-w-idx="' + hit.wordIndex + '"]');
            }
            if (!target) continue;

            var wrap = (editor.ownerDocument || document).createElement('span');
            wrap.className = 'ac-filler-pending';
            wrap.setAttribute('data-w-idx', String(hit.wordIndex));
            wrap.setAttribute('data-reason', hit.reason || 'filler');
            // Strikethrough rendering is CSS-driven; we still copy the text in
            // a child span so consumers can read the original token easily.
            var inner = (editor.ownerDocument || document).createElement('span');
            inner.className = 'ac-filler-text';
            inner.textContent = hit.text || target.textContent || '';
            wrap.appendChild(inner);

            var pill = (editor.ownerDocument || document).createElement('button');
            pill.type = 'button';
            pill.className = 'ac-filler-keep';
            pill.textContent = keepLabel;
            pill.setAttribute('aria-label', keepAria.replace('{text}', hit.text || ''));
            pill.setAttribute('data-w-idx', String(hit.wordIndex));
            pill.addEventListener('click', function(idx){
                return function(ev){
                    ev.preventDefault();
                    ev.stopPropagation();
                    toggleKeep(idx);
                };
            }(hit.wordIndex));
            wrap.appendChild(pill);

            target.parentNode.replaceChild(wrap, target);
            current.decoratedNodes.push({ originalNode: target, replacement: wrap, wordIndex: hit.wordIndex });
            decorated++;
        }
        return decorated;
    }

    function toggleKeep(wordIndex) {
        if (!current.keep) current.keep = new Set();
        if (current.keep.has(wordIndex)) {
            current.keep.delete(wordIndex);
        } else {
            current.keep.add(wordIndex);
        }
        // Mirror onto the DOM wrapper so CSS can flip strike-through off for
        // user-kept entries without a full re-render.
        if (typeof document !== 'undefined') {
            var nodes = document.querySelectorAll('.ac-filler-pending[data-w-idx="' + wordIndex + '"]');
            for (var i = 0; i < nodes.length; i++) {
                nodes[i].classList.toggle('kept', current.keep.has(wordIndex));
            }
        }
    }

    function getPendingKeepIndices() {
        return current.keep ? new Set(current.keep) : new Set();
    }

    function clearDecorations(editor) {
        editor = editor || (typeof document !== 'undefined' && document.getElementById('ac-editor'));
        if (!editor) return;
        var pending = editor.querySelectorAll('.ac-filler-pending');
        for (var i = 0; i < pending.length; i++) {
            var node = pending[i];
            // Try to restore the original span we replaced — fall back to a
            // plain text node if the original is missing (e.g. mutated since).
            var idx = node.getAttribute('data-w-idx');
            var match = null;
            for (var d = 0; d < current.decoratedNodes.length; d++) {
                if (String(current.decoratedNodes[d].wordIndex) === String(idx)) {
                    match = current.decoratedNodes[d].originalNode;
                    break;
                }
            }
            if (match) {
                node.parentNode.replaceChild(match, node);
            } else {
                // Plain text node fallback — preserve the visible text.
                var textInner = node.querySelector('.ac-filler-text');
                var plain = (editor.ownerDocument || document).createTextNode(textInner ? textInner.textContent : (node.textContent || ''));
                node.parentNode.replaceChild(plain, node);
            }
        }
    }

    /**
     * applyPlan(words, plan, opts)
     *   - words: the source words array (state.words)
     *   - plan: the plan returned by detectPlan; if null, we'll re-detect
     *   - opts.keepIndices: override the in-memory keep-set (tests pass this
     *     directly so they don't have to simulate pill clicks). Falls back to
     *     getPendingKeepIndices().
     *   - opts.cleanerOpts: passed through to __acCleaner.apply (trimSilences,
     *     silenceThresholdSec, lang, customFillers).
     *
     * Returns the result of __acCleaner.apply.
     */
    function applyPlan(words, plan, opts) {
        opts = opts || {};
        var keep = opts.keepIndices != null ? opts.keepIndices : getPendingKeepIndices();
        // Accept Set, Array, or null.
        var keepArr;
        if (keep && typeof keep.forEach === 'function' && !Array.isArray(keep)) {
            keepArr = [];
            keep.forEach(function(v){ keepArr.push(v); });
        } else if (Array.isArray(keep)) {
            keepArr = keep.slice();
        } else {
            keepArr = [];
        }
        var cleanerOpts = Object.assign({}, opts.cleanerOpts || {}, {
            keepWordIndices: keepArr,
        });
        if (!window.__acCleaner || typeof window.__acCleaner.apply !== 'function') {
            return { words: words, removedWords: 0, secsCut: 0, removedRanges: [] };
        }
        var result = window.__acCleaner.apply(words, plan, cleanerOpts);
        // Reset preview state once committed.
        cancelPreview();
        return result;
    }

    /**
     * cancelPreview()
     *   - removes inline decorations from #ac-editor
     *   - clears the in-memory plan + keep-set
     *   - hides the review banner if present (#ac-filler-preview-banner)
     */
    function cancelPreview() {
        if (typeof document !== 'undefined') {
            clearDecorations(document.getElementById('ac-editor'));
            var banner = document.getElementById('ac-filler-preview-banner');
            if (banner) banner.hidden = true;
        }
        current.plan = null;
        current.keep = null;
        current.decoratedNodes = [];
    }

    var api = {
        isEnabled: isEnabled,
        setEnabled: setEnabled,
        detectPlan: detectPlan,
        renderInlineDecorations: renderInlineDecorations,
        applyPlan: applyPlan,
        cancelPreview: cancelPreview,
        getPendingKeepIndices: getPendingKeepIndices,
        // Test-only: expose internal current state for assertion. Not part of
        // the documented surface.
        _current: current,
    };

    if (typeof window !== 'undefined') window.__acFillerPreview = api;
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
})();
